import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import './FAQ.css';

const FAQ = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('de');
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setLanguage(user.language || 'de');
      } catch (e) {
        setLanguage(navigator.language?.startsWith('de') ? 'de' : 'en');
      }
    } else {
      setLanguage(navigator.language?.startsWith('de') ? 'de' : 'en');
    }
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = {
    de: [
      {
        question: 'Was ist StoryTales?',
        answer: 'StoryTales ist eine KI-gestützte Plattform zur Erstellung personalisierter Kindergeschichten. Mit Hilfe von Google Gemini für Texte und OpenAI DALL-E oder Google Nano Banana für Illustrationen erstellen wir einzigartige, magische Geschichten, in denen Ihr Kind zum Helden wird.'
      },
      {
        question: 'Wie funktioniert die KI-Generierung?',
        answer: 'Unsere KI nutzt modernste Sprachmodelle (Google Gemini 2.5 Pro) zur Textgenerierung und DALL-E 3 oder Nano Banana für wunderschöne Illustrationen. Sie geben einfach den Namen, das Alter Ihres Kindes und ein Thema ein – die KI erstellt dann eine vollständige Geschichte mit passenden Bildern.'
      },
      {
        question: 'Welche Abos gibt es?',
        answer: 'Wir bieten zwei Pläne: <br><strong>Kostenlos:</strong> 1 Geschichte pro Monat, PDF-Export<br><strong>Pro (4,99€/Monat):</strong> 3 Geschichten pro Woche, PDF/Print/Kindle-Versand, Anpassung von Haarfarbe & Augenfarbe, Foto-Upload zur Charakterbeschreibung'
      },
      {
        question: 'Sind die Geschichten für mein Kind geeignet?',
        answer: 'Wir passen die Textlänge und Komplexität automatisch an das Alter Ihres Kindes an. Die KI ist darauf trainiert, altersgerechte Inhalte zu erstellen. Wir empfehlen dennoch, jede Geschichte vor dem Vorlesen zu überprüfen, da KI gelegentlich unerwartete Inhalte erzeugen kann.'
      },
      {
        question: 'In welchen Sprachen sind Geschichten verfügbar?',
        answer: 'Aktuell unterstützen wir Deutsch und Englisch. Die Geschichten werden in der von Ihnen gewählten Sprache generiert.'
      },
      {
        question: 'Kann ich meine eigenen Themen vorschlagen?',
        answer: 'Ja! Als Pro-Nutzer können Sie benutzerdefinierte Themen eingeben. Die KI erstellt dann eine Geschichte basierend auf Ihren Vorgaben. Kostenlose Nutzer wählen aus vordefinierten Themen.'
      },
      {
        question: 'Wie funktioniert der Send-to-Kindle-Service?',
        answer: 'Pro-Nutzer können Geschichten direkt an ihre Kindle-E-Mail-Adresse senden. Sie finden diese in Ihren Amazon Kindle-Einstellungen. Wichtig: Fügen Sie info@storytales.xyz zu Ihrer Liste genehmigter E-Mail-Adressen hinzu.'
      },
      {
        question: 'Kann ich Geschichten löschen?',
        answer: 'Ja, Sie können jederzeit Geschichten aus Ihrem Dashboard löschen. Einmal gelöscht, können sie nicht wiederhergestellt werden.'
      },
      {
        question: 'Kann ich mein Konto löschen?',
        answer: 'Ja, Sie können Ihr Konto jederzeit in den Einstellungen löschen. Dies löscht alle Ihre Daten permanent gemäß DSGVO-Richtlinien.'
      },
      {
        question: 'Wie kann ich meine Daten exportieren?',
        answer: 'In den Einstellungen finden Sie eine "Daten exportieren"-Funktion. Sie erhalten eine JSON-Datei mit all Ihren Kontodaten und Geschichten (DSGVO Recht auf Datenübertragbarkeit).'
      },
      {
        question: 'Was passiert mit meinen Daten?',
        answer: 'Ihre Daten werden sicher gespeichert und nur zur Bereitstellung unseres Services verwendet. Wir geben keine Daten an Dritte weiter. Lesen Sie unsere Datenschutzerklärung für Details.'
      },
      {
        question: 'Kann ich mein Abo kündigen?',
        answer: 'Ja, Sie können Ihr Abonnement jederzeit kündigen. Es läuft bis zum Ende des bezahlten Zeitraums und wird dann nicht verlängert.'
      }
    ],
    en: [
      {
        question: 'What is StoryTales?',
        answer: 'StoryTales is an AI-powered platform for creating personalized children\'s stories. Using Google Gemini for text and OpenAI DALL-E or Google Nano Banana for illustrations, we create unique, magical stories where your child becomes the hero.'
      },
      {
        question: 'How does AI generation work?',
        answer: 'Our AI uses state-of-the-art language models (Google Gemini 2.5 Pro) for text generation and DALL-E 3 or Nano Banana for beautiful illustrations. You simply input your child\'s name, age, and a theme – the AI then creates a complete story with matching images.'
      },
      {
        question: 'What subscription plans are available?',
        answer: 'We offer two plans: <br><strong>Free:</strong> 2 total stories, PDF export<br><strong>Pro ($5.99/month):</strong> 4 stories per month, PDF/Print/Kindle delivery, hair & eye color customization, photo upload for character description'
      },
      {
        question: 'Are the stories appropriate for my child?',
        answer: 'We automatically adjust text length and complexity based on your child\'s age. The AI is trained to create age-appropriate content. However, we recommend reviewing each story before reading, as AI can occasionally produce unexpected content.'
      },
      {
        question: 'What languages are available?',
        answer: 'We currently support German and English. Stories are generated in your selected language.'
      },
      {
        question: 'Can I suggest my own themes?',
        answer: 'Yes! Pro users can enter custom themes. The AI will create a story based on your specifications. Free users choose from predefined themes.'
      },
      {
        question: 'How does Send to Kindle work?',
        answer: 'Pro users can send stories directly to their Kindle email address. You can find this in your Amazon Kindle settings. Important: Add info@storytales.xyz to your approved email list.'
      },
      {
        question: 'Can I delete stories?',
        answer: 'Yes, you can delete stories from your dashboard at any time. Once deleted, they cannot be recovered.'
      },
      {
        question: 'Can I delete my account?',
        answer: 'Yes, you can delete your account at any time in Settings. This permanently deletes all your data according to GDPR guidelines.'
      },
      {
        question: 'How can I export my data?',
        answer: 'In Settings, you\'ll find an "Export Data" function. You\'ll receive a JSON file with all your account data and stories (GDPR Right to Data Portability).'
      },
      {
        question: 'What happens to my data?',
        answer: 'Your data is securely stored and only used to provide our service. We don\'t share data with third parties. Read our Privacy Policy for details.'
      },
      {
        question: 'Can I cancel my subscription?',
        answer: 'Yes, you can cancel your subscription at any time. It will run until the end of the paid period and then not renew.'
      }
    ]
  };

  const faqs = faqData[language] || faqData.en;

  return (
    <div className="faq-page">
      <div className="faq-header-banner">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="back-button"
        >
          ← {language === 'de' ? 'Zurück' : 'Back'}
        </Button>
      </div>

      <div className="faq-container">
        <h1>{language === 'de' ? 'Häufig gestellte Fragen' : 'Frequently Asked Questions'}</h1>
        <p className="faq-subtitle">
          {language === 'de' 
            ? 'Hier finden Sie Antworten auf die häufigsten Fragen zu StoryTales.'
            : 'Find answers to the most common questions about StoryTales.'}
        </p>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button 
                className={`faq-question ${openIndex === index ? 'active' : ''}`}
                onClick={() => toggleFAQ(index)}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
              </button>
              {openIndex === index && (
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: faq.answer }} />
              )}
            </div>
          ))}
        </div>

        <div className="faq-contact">
          <h2>{language === 'de' ? 'Weitere Fragen?' : 'More Questions?'}</h2>
          <p>
            {language === 'de'
              ? 'Wir helfen gerne! Kontaktieren Sie uns unter '
              : 'We\'re happy to help! Contact us at '}
            <a href="mailto:info@storytales.xyz">info@storytales.xyz</a>
            {language === 'de' ? ' oder nutzen Sie unser ' : ' or use our '}
            <a href="/contact">{language === 'de' ? 'Kontaktformular' : 'contact form'}</a>.
          </p>
        </div>
      </div>

      <Footer language={language} />
    </div>
  );
};

export default FAQ;
